// WbOpenTicketsPage — /weighbridge/tickets/open (Phase C9).
//
// Operator console view: vehicles awaiting their second weigh plus
// currently open tickets. Both sections poll every 30 s while the page
// is mounted (paused when the tab is backgrounded).

import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { extractErrorMessage } from '../../../utils/errorMessage';
import {
  useWbTickets,
  useWbTicketsAwaitingSecond,
  type AwaitingSecondWeighDto,
  type TicketDirection,
  type TicketSummaryDto,
} from '../hooks/useWbTickets';
import styles from './WbTicketsPage.module.css';

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function directionBadge(d: TicketDirection): JSX.Element {
  return <Badge>{titleCase(d)}</Badge>;
}

function fmtTimeOnSite(min: number): string {
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

function timeOnSiteStyle(min: number): CSSProperties | undefined {
  if (min > 120) return { color: 'var(--color-status-unpaid_text, #991b1b)' };
  if (min > 60) return { color: 'var(--color-warning-text, #b45309)' };
  return undefined;
}

export function WbOpenTicketsPage(): JSX.Element {
  const navigate = useNavigate();
  const [openPage, setOpenPage] = useState<number>(1);

  const awaitingQuery = useWbTicketsAwaitingSecond();
  const awaitingRows = awaitingQuery.data?.tickets ?? [];

  const openQuery = useWbTickets(
    { status: 'open', page: openPage, per_page: 25 },
    { enablePolling: true },
  );
  const openRows = openQuery.data?.tickets ?? [];
  const openPagination = openQuery.data?.pagination;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Open Tickets"
        subtitle="Tickets currently active on the weighbridge."
      />

      {/* -- Awaiting Second Weigh ------------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Awaiting Second Weigh</h2>

        {awaitingQuery.isError ? (
          <p className={styles.error}>
            {extractErrorMessage(awaitingQuery.error)}
          </p>
        ) : null}

        <Card flush>
          {awaitingQuery.isLoading ? (
            <div className={styles.loadingRow}>
              <Spinner label="Loading" />
            </div>
          ) : awaitingRows.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No vehicles awaiting second weigh."
            />
          ) : (
            <table
              className={styles.table}
              data-testid="awaiting-second-table"
            >
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>VRM</th>
                  <th>Direction</th>
                  <th>Yard</th>
                  <th className={styles.numCol}>Gross weight</th>
                  <th>Time on site</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {awaitingRows.map((t: AwaitingSecondWeighDto) => (
                  <tr key={t.enc_id}>
                    <td>{t.customer.name}</td>
                    <td>{t.vehicle_reg_snapshot ?? '—'}</td>
                    <td>{directionBadge(t.direction)}</td>
                    <td>{t.yard.name}</td>
                    <td className={styles.numCol}>
                      {t.gross_weight_kg.toLocaleString('en-GB')} kg
                    </td>
                    <td style={timeOnSiteStyle(t.time_on_site_minutes)}>
                      {fmtTimeOnSite(t.time_on_site_minutes)}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/weighbridge/tickets/${t.enc_id}`)
                        }
                      >
                        Second weigh
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>

      {/* -- Open -------------------------------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Open</h2>

        {openQuery.isError ? (
          <p className={styles.error}>
            {extractErrorMessage(openQuery.error)}
          </p>
        ) : null}

        <Card flush>
          {openQuery.isLoading ? (
            <div className={styles.loadingRow}>
              <Spinner label="Loading" />
            </div>
          ) : openRows.length === 0 ? (
            <EmptyState icon={Receipt} title="No open tickets." />
          ) : (
            <table
              className={styles.table}
              data-testid="open-tickets-table"
            >
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Customer</th>
                  <th>VRM</th>
                  <th>Direction</th>
                  <th>Yard</th>
                  <th>Opened</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {openRows.map((t: TicketSummaryDto) => (
                  <tr key={t.enc_id}>
                    <td>
                      {t.ticket_number === null ? (
                        <span className={styles.draftMarker}>Draft</span>
                      ) : (
                        t.ticket_number
                      )}
                    </td>
                    <td>{t.customer.name}</td>
                    <td>{t.vehicle_reg_snapshot ?? '—'}</td>
                    <td>{directionBadge(t.direction)}</td>
                    <td>{t.yard.name}</td>
                    <td>{fmtDate(t.created_at)}</td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(`/weighbridge/tickets/${t.enc_id}`)
                        }
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {openPagination &&
        openPagination.total > openPagination.per_page ? (
          <div className={styles.pagination}>
            <span>
              Page {openPagination.page} of {openPagination.total_pages} ·{' '}
              {openPagination.total} ticket
              {openPagination.total === 1 ? '' : 's'}
            </span>
            <div className={styles.paginationButtons}>
              <Button
                variant="secondary"
                size="sm"
                disabled={openPagination.page <= 1}
                onClick={() => setOpenPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={openPagination.page >= openPagination.total_pages}
                onClick={() =>
                  setOpenPage((p) =>
                    Math.min(openPagination.total_pages, p + 1),
                  )
                }
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
